import type { Maybe } from "@entrilia/shared/types.ts";
import {
  FieldConfigurationType,
  MeasureDateRange,
  type BiAggregation,
  type FieldConfiguration,
} from "../../../../../api/types/fieldConfigurationTypes.ts";
import type {
  AnyDescriptor,
  ConditionField,
  CustomColumnDescriptor,
  CustomColumnField,
  DimensionDescriptor,
  DimensionField,
  MeasureField,
  MetricField,
  ReportField,
} from "../../../../../api/types/svTypes.ts";
import {
  AreaItemType,
  DimensionFieldType,
  ItemDataType,
  MeasureUsage,
  MetaIcons,
  MetaItemType,
} from "../../../../../api/types/svTypes.ts";
import type { CustomColumnData } from "../components/builder/common/fields/custom-field/customColumn.types.ts";
import { supportsFormatting } from "../components/builder/common/fields/custom-field/utilities/calculationTypeCapabilities.ts";
import { createFieldId, keepOnlyLettersAndNumbers } from "../components/builder/common/utilities/createFieldId.ts";
import { isNumericField } from "../components/builder/utils/isNumericField.ts";
import { defaultDateFormat } from "../formatters/NumberFormatter.ts";
import { getDefaultFormatting } from "./aggregationHelper.ts";

export const newDimensionMeta = (changes: Partial<DimensionDescriptor>) => {
  const meta: DimensionDescriptor = {
    availableFields: [],
    caption: "",
    dictionary: [],
    displayValues: [],
    icon: MetaIcons.Calendar,
    isNew: false,
    keyFieldName: "",
    name: "",
    type: ItemDataType.General,
    itemType: MetaItemType.DIMENSION,
    glAllowed: true,
    ...changes,
  };
  return meta;
};

export const newCustomColumnField = (data: CustomColumnData): CustomColumnField => {
  const config = {
    type: FieldConfigurationType.Custom,
    guid: createFieldId(keepOnlyLettersAndNumbers(data.name)),
    name: data.name,
    formulaParameters: data.selectedFormulaParameterIds,
    calculationType: data.selectedCalculationType,
    format: data.format,
    formula: data.formula,
    blankIfMissingValue: data.blankIfMissingValue,
  };
  const field: CustomColumnField = {
    type: DimensionFieldType.CUSTOM,
    meta: {
      caption: data.name,
      name: data.name,
      type: ItemDataType.Numeric,
      itemType: MetaItemType.CUSTOM,
      icon: MetaIcons.CustomCalculation,
      group: "",
      usage: MeasureUsage.General,
      configuration: config,
    },
    areaItemType: AreaItemType.FIELDS,
    config: config,
  };
  return field;
};

export const buildCustomColumnConfigChanges = (data: CustomColumnData): Partial<FieldConfiguration> => ({
  calculationType: data.selectedCalculationType,
  customLabel: data.name,
  formulaParameters: data.selectedFormulaParameterIds,
  formula: data.formula,
  format: data.format,
  blankIfMissingValue: data.blankIfMissingValue,
});

export const isMeasure = (field: ReportField | undefined | null): field is MeasureField => {
  if (field === undefined || field === null) {
    return false;
  }
  return field.config.type === FieldConfigurationType.Irr || field.config.type === FieldConfigurationType.Measure;
};

export const isMetric = (field: ReportField | undefined | null): field is MetricField => {
  if (field === undefined || field === null) {
    return false;
  }
  return field.config.type === FieldConfigurationType.Metric;
};

export const isIrr = (field: ReportField | undefined | null): field is MeasureField => {
  if (field === undefined || field === null) {
    return false;
  }
  return field.config.type === FieldConfigurationType.Irr;
};

export const isAggregation = (field: ReportField | undefined | null): boolean => {
  return isAggregatedType(field?.config);
};

export const isPersistedAggregation = (field: ReportField | undefined | null): boolean => {
  return isAggregation(field) && !!field?.config.persistedAggregationName;
};

export const isAggregationFieldConfiguration = (config: Maybe<FieldConfiguration>): boolean => {
  return isAggregatedType(config);
};

export const isAggregatedType = (config: Maybe<Partial<FieldConfiguration>>) => {
  return config?.type === FieldConfigurationType.Aggregation;
};

export const isNotAggregatedType = (config: Maybe<Partial<FieldConfiguration>>) => {
  return config?.type !== FieldConfigurationType.Aggregation || config.aggregation === undefined;
};

export function isCustomColumn(field: ReportField | undefined | null): field is CustomColumnField;
export function isCustomColumn(config: Maybe<Partial<FieldConfiguration>>): boolean;
export function isCustomColumn(meta: AnyDescriptor): meta is CustomColumnDescriptor;
export function isCustomColumn(arg: ReportField | Maybe<Partial<FieldConfiguration>> | AnyDescriptor): boolean {
  if (arg == null) return false;
  if ("itemType" in arg) return arg.itemType === MetaItemType.CUSTOM;
  if ("config" in arg) return arg.config.type === FieldConfigurationType.Custom;
  return arg.type === FieldConfigurationType.Custom;
}

export const hasValidConditionReference = (field: CustomColumnField, conditions: ConditionField[]): boolean => {
  const conditionGuid = field.config.formulaParameters?.[0];
  if (!conditionGuid) {
    return false;
  }
  return conditions.some(
    (condition) => condition.config.guid === conditionGuid && condition.validation?.invalid !== true
  );
};

export const canBeReferencedInCustomFormula = (field: ReportField | undefined | null): boolean => {
  if (!field) {
    return false;
  }
  if (isCustomColumn(field) || isMetric(field)) {
    return false;
  }
  return isNumericField(field);
};

export const isEligibleDateScopeField = (field: ReportField | undefined | null): boolean => {
  return isMeasure(field) && !field.meta.capabilities?.isScalar && !isIrr(field);
};

export const isDateAggregationField = (field: ReportField | undefined | null): boolean => {
  return isAggregation(field) && !!field?.config.dependsFromFieldId;
};

export const newDateAggregationField = (
  dimensionMeta: DimensionDescriptor,
  aggregation: BiAggregation,
  dependsFromFieldId: string,
  label: string
): DimensionField => {
  const config = {
    type: FieldConfigurationType.Aggregation,
    guid: createFieldId(keepOnlyLettersAndNumbers(label)),
    name: dimensionMeta.name,
    aggregation,
    dependsFromFieldId,
    customLabel: label,
    dateRange: MeasureDateRange.EndingBalance,
    format: defaultDateFormat,
  };
  return {
    type: DimensionFieldType.DIMENSION,
    areaItemType: AreaItemType.FIELDS,
    meta: {
      ...dimensionMeta,
      itemType: MetaItemType.DIMENSION,
      keyFieldName: "",
      isNew: false,
      availableFields: [],
      displayValues: [],
      dictionary: [],
      glAllowed: false,
    },
    config,
  };
};

export const isAggregationBased = (field: ReportField | undefined | null): field is MeasureField => {
  return isMeasure(field) || isMetric(field) || isAggregation(field);
};

export const isDimension = (field: ReportField | undefined | null): field is DimensionField => {
  return field?.config?.type === FieldConfigurationType.Dimension;
};

export const isDimensionFieldConfiguration = (config: Maybe<FieldConfiguration>) => {
  return config?.type === FieldConfigurationType.Dimension;
};

export const isNotAggregatedDimension = (field: Maybe<ReportField>): field is DimensionField => {
  return isDimension(field) && isNotAggregatedType(field.config);
};

export const isDimensionBased = (field: ReportField | undefined | null): field is DimensionField => {
  if (field === undefined || field === null) {
    return false;
  }
  return isDimensionBasedType(field.config.type);
};

export const isDimensionBasedType = (type: FieldConfigurationType) => {
  return type === FieldConfigurationType.Dimension || type === FieldConfigurationType.Aggregation;
};

export const getAggregationBasedFields = (fields: ReportField[]) => {
  return fields.filter((f) => isAggregationBased(f));
};

export const getCustomFields = (fields: ReportField[]) => {
  return fields.filter((f) => isCustomColumn(f));
};

export const ensureDefaultFormatIsSet = (field: ReportField): ReportField => {
  if (
    !field.config.format &&
    (isDimensionBased(field) || (isCustomColumn(field) && supportsFormatting(field.config.calculationType)))
  ) {
    return {
      ...field,
      config: {
        ...field.config,
        format: getDefaultFormatting(field),
      },
    };
  }

  return field;
};
